import React from "react";
import Tooltip from "@mui/material/Tooltip";
import StatusPill from "./StatusPill";
import { TaskHistoryTooltip, TaskCommentTooltip } from "./TaskRowTooltips";
import { dueLabel, durationLabel, taskSubline, timeOnly, workElapsedMs, workClockIsProvisional } from "./taskHubUtils";

// The Now list: everything that can be done right now, one flat list sorted
// by due, overdue rows tinted inline. The whole row is the tap target; every
// action lives in what it opens (no button on the row).

// Task | Status | Due | Progress | Who. The task name truncates, so it does
// not need the widest track; Due carries two lines and Progress a bar plus
// a step count and clock.
const GRID = "grid-cols-[1.6fr_1fr_1.3fr_1.4fr_1.2fr]";

function Avatars({ assignees }) {
  if (!assignees?.length) return null;
  const shown = assignees.slice(0, 3);
  // Grey initials everywhere a person appears on a task (the drawer's slot
  // rows match), not role colours.
  return (
    <span className="flex items-center">
      {shown.map((a) => (
        <span
          key={a.store_team_member_id}
          title={a.full_name}
          className="bg-slate-200 text-slate-700 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white -ml-1.5 first:ml-0 text-[9px] font-semibold"
        >
          {a.initials}
        </span>
      ))}
      {assignees.length > 3 && (
        <span className="bg-slate-400 text-white w-5 h-5 rounded-full flex items-center justify-center border-2 border-white -ml-1.5 text-[9px] font-semibold">
          +{assignees.length - 3}
        </span>
      )}
    </span>
  );
}

function Who({ row }) {
  const assignees = row.assignees || [];
  if (assignees.length === 0) {
    return <span className="text-slate-500 truncate">{row.eligible_label || "Anyone"}</span>;
  }
  const names = assignees.map((a) => a.full_name).filter(Boolean);
  const label =
    names.length === 1
      ? names[0]
      : names.length === 2
      ? `${names[0]}, ${names[1]}`
      : `${names[0]} +${names.length - 1}`;
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <Avatars assignees={assignees} />
      <span className="truncate text-slate-700">
        {label}
      </span>
    </span>
  );
}

function Row({ row, now, tz, storeId, showHistory, showComments, onOpenTask, fetchedAtMs, nowMs, readOnly = false }) {
  const due = dueLabel(row, now, tz);
  const tracked = !!row.plan_task?.track_time;
  const workMs = tracked ? workElapsedMs(row, fetchedAtMs, nowMs) : 0;
  const stepsTotal = row.steps_total || 0;
  const stepsDone = row.steps_done || 0;
  const pct = stepsTotal ? Math.round((100 * stepsDone) / stepsTotal) : row.status === "completed" ? 100 : 0;
  // The status pills' colours: emerald done, amber waiting on a manager,
  // violet while the work is in progress.
  const barColor =
    row.status === "completed" ? "bg-emerald-600" : row.status === "waiting_validation" ? "bg-amber-500" : "bg-violet-500";
  return (
    <div
      onClick={readOnly ? undefined : () => onOpenTask(row)}
      className={`grid ${GRID} gap-3 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 text-[13px] ${readOnly ? "opacity-70" : "cursor-pointer hover:bg-slate-50"} ${
        due.overdue ? "bg-rose-50/40" : ""
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{row.plan_task?.name}</span>
          <span className="flex items-center gap-2 flex-none">
            {showHistory && !readOnly && (
              <TaskHistoryTooltip
                storeId={row.store_id || storeId}
                occurrenceUuid={row.plan_task_assignment_uuid}
                occurrenceDate={row.date}
                tz={tz}
                onClick={() => onOpenTask(row, "timeline")}
              />
            )}
            {showComments && !readOnly && (
              <TaskCommentTooltip
                storeId={row.store_id || storeId}
                occurrenceUuid={row.plan_task_assignment_uuid}
                occurrenceDate={row.date}
                commentCount={row.comment_count || 0}
                onClick={() => onOpenTask(row, "conversation")}
              />
            )}
          </span>
        </span>
        <span className="block text-[11px] text-slate-500 truncate">{taskSubline(row.plan_task)}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <StatusPill status={row.status} paused={row.paused} />
      </span>
      <span className="min-w-0">
        <span className={`block tabular-nums ${due.overdue ? "font-semibold text-rose-600" : "text-slate-700"}`}>
          {due.primary}
        </span>
        {due.secondary && (
          <span className={`block text-[11px] ${due.overdue ? "text-rose-600" : "text-slate-500"}`}>
            {due.secondary}
          </span>
        )}
        {!due.secondary && row.submitted_at && (
          <span className="block text-[11px] text-slate-500">
            Submitted {timeOnly(new Date(row.submitted_at), tz)}
          </span>
        )}
      </span>
      <span className="flex items-center gap-2 min-w-0">
        {/* The bar takes a fixed share of the cell (capped), so every row's
            bar lines up whatever the label beside it says, and it still
            shrinks with the column on a narrow layout instead of pushing the
            label into the next column. The label truncates before it wraps. */}
        <span className="w-[55%] max-w-[96px] flex-none h-1.5 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
          <span className={`block h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
        </span>
        <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap min-w-0 truncate">
          {stepsDone}/{stepsTotal}
          {tracked && (
            <>
              {" · "}
              {row.work_estimated || workClockIsProvisional(row, nowMs) ? "~" : ""}
              {durationLabel(workMs)}
            </>
          )}
        </span>
      </span>
      <Who row={row} />
    </div>
  );
}

// showHistory: the history icon opens a Timeline tab, which only the manager
// drawer has; the employee list leaves it out.
// showComments: the comment icon opens the thread (the kiosk included, where
// the thread posts as the person who identified).
export default function NowTable({ data, storeId, showDone, onToggleDone, showUpcoming, onToggleUpcoming, onOpenTask, onGoOverview, showHistory = true, showComments = true }) {
  const now = data?.now ? new Date(data.now) : new Date();
  const tasks = data?.tasks || [];
  const done = data?.done_today || [];
  const notYet = data?.not_yet || [];
  const tz = data?.timezone;
  // Time on task ticks for running rows: remember when this payload arrived
  // and re-render once a minute so the label keeps pace with the server.
  const [fetchedAtMs, setFetchedAtMs] = React.useState(() => Date.now());
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    setFetchedAtMs(Date.now());
    setNowMs(Date.now());
  }, [data]);
  React.useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);
  // Rows that open later today (or later this period); shown on request,
  // never openable.
  const upcoming = data?.upcoming_today || [];
  const upcomingCount = data?.not_yet_count ?? notYet.length;
  const doneCount = data?.done_today_count ?? done.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">
            {tasks.length} task{tasks.length === 1 ? "" : "s"} can be done now
          </span>
          {" · "}
          {now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: tz || undefined })}, {timeOnly(now, tz)}
          {" · sorted by due"}
        </div>
        <div className="flex items-center gap-5">
          {onToggleUpcoming && (
            <label className="flex items-center gap-2 text-[13px] text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-violet-700"
                checked={!!showUpcoming}
                onChange={(e) => onToggleUpcoming(e.target.checked)}
              />
              Show upcoming today ({upcomingCount})
            </label>
          )}
          <label className="flex items-center gap-2 text-[13px] text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-violet-700"
              checked={!!showDone}
              onChange={(e) => onToggleDone(e.target.checked)}
            />
            Show done today ({doneCount})
          </label>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
        <div className={`grid ${GRID} gap-3 items-center px-4 py-2.5 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-400`}>
          <span>Task</span>
          <span>Status</span>
          <span>Due</span>
          <span>Progress</span>
          <span>Who</span>
        </div>
        {tasks.length === 0 && (!showDone || done.length === 0) && (!showUpcoming || upcoming.length === 0) ? (
          <div className="px-4 py-10 text-center">
            <div className="text-sm font-semibold text-slate-700">Nothing to do right now</div>
            <div className="text-xs text-slate-500 mt-1">
              {doneCount > 0
                ? `${doneCount} task${doneCount === 1 ? "" : "s"} already done today.`
                : "Routines show up here when they become available, one-offs as soon as they are published."}
            </div>
          </div>
        ) : (
          <>
            {tasks.map((row) => (
              <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} showHistory={showHistory} showComments={showComments} onOpenTask={onOpenTask} fetchedAtMs={fetchedAtMs} nowMs={nowMs} />
            ))}
            {showDone && done.length > 0 && (
              <>
                <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Done today
                </div>
                {done.map((row) => (
                  <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} showHistory={showHistory} showComments={showComments} onOpenTask={onOpenTask} fetchedAtMs={fetchedAtMs} nowMs={nowMs} />
                ))}
              </>
            )}
            {showUpcoming && upcoming.length > 0 && (
              <>
                <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Upcoming today
                </div>
                {upcoming.map((row) => (
                  <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} showHistory={showHistory} showComments={showComments} onOpenTask={onOpenTask} fetchedAtMs={fetchedAtMs} nowMs={nowMs} readOnly />
                ))}
              </>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-end mt-3 text-[13px] text-slate-500 px-1">
        {onGoOverview && (
          <button onClick={onGoOverview} className="text-violet-700 font-semibold hover:underline whitespace-nowrap ml-4">
            See Overview
          </button>
        )}
      </div>
    </div>
  );
}
